import { useState } from "react";
import useSWR from "swr";
import { fetcher } from "../../../../services/fetcher";
import { Flex, Button, Paper, Box, Center, Image, Drawer, Text } from "@mantine/core";
import { useToggle } from "@mantine/hooks";
import { useOutletContext } from "react-router-dom";
import DataTable from "../../../../common/components/DataTable/DataTable";
import {
  errorNotifications,
  successNotifications,
} from "../../../../utils/handleNotifications";
import { AssignCover_API, CustomizeCovers_API, RemoveAssignCover_API } from "../../../../api/endpoints";
import MainLoader from "../../../../common/components/Loader/Loader";

const AssignCover = () => {
  const outletContext = useOutletContext();
  const user = outletContext ? outletContext.user : null;
  const [value, toggle] = useToggle(["add", "view"]);
  const [currentSelection, setCurrentSelection] = useState([]);
  const [selectedPlans, setSelectedPlans] = useState([]); 
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [activeCoverId, setActiveCoverId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const userEmail = user ? user.user_profile_info.user.email : null;
  const AssignCover_APIUrl = `${AssignCover_API}?user_email=${userEmail}`;
  
  const {
    data: plans,
    error: plansError,
    isLoading: isPlansLoading,
    mutate: mutatePlans,
  } = useSWR(AssignCover_APIUrl, fetcher);

  const {
    data: covers,
    error: coversError,
    isLoading: isCoversLoading,
  } = useSWR(plans ? CustomizeCovers_API : null, fetcher, {
    dedupingInterval: 9000,
    revalidateOnFocus: false,
  });

  const tableConfig = {
    fields: [
      { key: "title", label: "Name" },
      { key: "cover_scope", label: "Scope" },
      { key: "currency", label: "Currency" },
      { key: "price", label: "Price" },
    ],
    selectionCheckbox: true,
    scrollable: true,
  };

  const handleSelection = (newSelection) => {
    setCurrentSelection(newSelection);
  };

  const handleAssignCover = async () => {
    if (!user) {
      return;
    }

    if (currentSelection.length === 0) {
      return errorNotifications("Please select covers");
    }

    try {
      const customizeCoverIds = currentSelection.filter(id =>
        covers.customize_covers.some(cover => cover.id === id)
      );

      const currentCustomize = Array.isArray(plans.customize_covers)
        ? plans.customize_covers.map(customize => customize.id)
        : [];

      const payload = {
        user_email: user.user_profile_info.user.email,
        customize_cover_ids: [...currentCustomize, ...customizeCoverIds],
      };

      await fetcher("issuance/assign-plan/", "POST", payload);
      await mutatePlans();
      toggle();
      successNotifications("Success, Covers assigned");
    } catch (error) {
      console.log(error);
      errorNotifications(error.response?.data || "An error occurred", error);
    }
  };

  const handleRemovePlan = async () => {
    if (!user) return;

    if (selectedPlans.length === 0) {
      return errorNotifications("Please select plans to remove");
    }

    try {
      const payload = {
        user_id: user.user_profile_info.user.id,
        cover_ids: selectedPlans,
      };

      await fetcher(RemoveAssignCover_API, "DELETE", payload);
      await mutatePlans();
      // toggle();
      successNotifications("Success, Plans removed");
    } catch (error) {
      console.log(error);
      errorNotifications(error.response?.data || "An error occurred", error);
    }
  };

  const handleRowClick = (row) => {
    if (!row?.id) return;
    setActiveCoverId(row.id);
    setDrawerOpened(true);
  };

  const { data: coverDetails, error: coverDetailsError, isLoading: isCoverLoading } = useSWR(
    activeCoverId ? `cover-api/customize-covers/${activeCoverId}/` : null,
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 15000 }
  );

  const brochureName = coverDetails?.brochure_name || coverDetails?.brochure || "";
  const isDev = import.meta.env.MODE === "development";
  const baseURL = isDev ? import.meta.env.VITE_API_ENDPOINT : "";
  const isAbsolute = /^https?:\/\//i.test(brochureName);
  const resolvedUrl = brochureName ? (isAbsolute ? brochureName : `${baseURL}${brochureName}`) : "";

  if (isPlansLoading) {
    return <MainLoader />;
  }

  if (plansError) {
    return <p>Failed to load plans</p>;
  }

  const assignedCovers = Array.isArray(plans.customize_covers)
    ? plans.customize_covers
    : [];

  const combinedData = covers
    ? covers.customize_covers.filter(
        cover => !assignedCovers.some(assignedCover => assignedCover.id === cover.id)
      )
    : [];

  const filterCovers = (items) => {
    if (!searchQuery) return items;
    const query = searchQuery.toLowerCase();
    return items.filter((cover) => {
      const title = String(cover?.title || "").toLowerCase();
      const scope = String(cover?.cover_scope || "").toLowerCase();
      const currency = String(cover?.currency || "").toLowerCase();
      const price = String(cover?.price || "").toLowerCase();
      return (
        title.includes(query) ||
        scope.includes(query) ||
        currency.includes(query) ||
        price.includes(query)
      );
    });
  };

  const filteredAssignedCovers = filterCovers(assignedCovers);
  const filteredCombinedData = filterCovers(combinedData);

  const EmptyComponent = () => (
    <Paper shadow="xl" style={{ backgroundColor: "#fff" }}>
      <Box style={{ textAlign: "center", padding: "20vh" }}>
        <Center>
          <Image
            src="/images/add-icon.png"
            width={80}
            height={114}
            alt="avatar"
          />
        </Center>
        <p>No covers available.</p>
          <Button variant="outline" onClick={() => toggle()}>
            {value === "add" ? "+ Assign Covers" : "View Covers"}
          </Button>
      </Box>
    </Paper>
  );

  return (
    <>
      <Drawer
        opened={drawerOpened}
        onClose={() => setDrawerOpened(false)}
        position="right"
        size="50%"
        title="Brochure"
        styles={{
          body: {
            flex: 1,
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
          content: { display: "flex", flexDirection: "column" },
        }}
      >
        {isCoverLoading ? (
          <Box p="md">
            <Text size="sm">Loading brochure...</Text>
          </Box>
        ) : resolvedUrl ? (
          <iframe
            title="Cover brochure"
            src={resolvedUrl}
            style={{
              border: 0,
              width: "100%",
              height: "100%",
              maxWidth: 900,
              maxHeight: "100%",
            }}
          />
        ) : coverDetailsError ? (
          <Box p="md">
            <Text size="sm">Failed to load brochure.</Text>
          </Box>
        ) : (
          <Box p="md">
            <Text size="sm">No brochure available.</Text>
          </Box>
        )}
      </Drawer>
      <Flex
        mih={50}
        gap="md"
        justify="flex-end"
        align="center"
        direction="row"
        wrap="wrap"
        mt={"lg"}
      >
        <Button variant="outline" onClick={() => toggle()}>
          {value === "add" ? "+ Assign Covers" : "View Covers"}
        </Button>
        <Button variant="outline" onClick={handleRemovePlan} disabled={selectedPlans.length === 0}>
          - Remove Plan
        </Button>
      </Flex>

      {value === "add" ? (
        assignedCovers.length === 0 ? (
          <EmptyComponent />
        ) : (
          <DataTable
            config={{ ...tableConfig, onRowClick: handleRowClick }}
            data={filteredAssignedCovers}
            heading={"Assigned Covers"}
            onSelectionChange={setSelectedPlans}
            onSearch={(event) => setSearchQuery(event.target.value)}
          />
        )
      ) : (
        <DataTable
          config={{
            ...tableConfig,
            selectionActions: [{ label: "Save", handler: handleAssignCover }],
            onRowClick: handleRowClick,
          }}
          data={coversError ? [] : filteredCombinedData}
          heading={"Select Covers"}
          loading={isCoversLoading}
          onSelectionChange={handleSelection}
          onSearch={(event) => setSearchQuery(event.target.value)}
        />
      )}
    </>
  );
};

export default AssignCover;